/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T21:10:00+05:00
Intent: The live toast and browser notification show the header in the panel language.
*/
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from './shared.service';
import { PagesService } from './pages.service';
import { SecureStorageService } from './secure-storage.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { formatPlanUpdateMessage } from './plan-update-message';
import { escapeHtml } from '../utils/html-escape.util';
import { localizedNotificationTitle } from '../utils/notification-text.util';

interface UserData {
    id: number;
    country?: string;
    created_at: number;
    Status?: string;
    companyIds?: number[];  // Array of company IDs owned by user
}

interface NotificationData {
    // uuid from the microservice; a real-time-only entry still gets a negative NUMBER as a
    // stand-in id, so this is deliberately both.
    id: number | string;
    created_at: number;
    type?: string;
    notification_name: string;
    notification_title: string;
    message: string;
    attachments?: any;
    deep_link?: string;
    custom_deep_link?: string;
    send_to?: string;
    custom_segment?: string;
    location?: string[];
    client_ids?: number[];
    _notification_logs_of_notifications?: any;
}

@Injectable({
    providedIn: 'root'
})
export class NotificationsService {
    totalNewNotifications = new BehaviorSubject<number>(0);
    realtimeNotifications = new BehaviorSubject<NotificationData[]>([]);

    sharedService = inject(SharedService);
    pagesService = inject(PagesService);
    private secureStorage = inject(SecureStorageService);
    private translate = inject(TranslateService, { optional: true });
    notif: any[] = [];

    // Track real-time notifications separately so they survive API fetches
    private realtimeOnlyNotifs: NotificationData[] = [];

    /** The live channel: one WebSocket to the gateway, authenticated as this session. */
    private socket: WebSocket | null = null;
    private socketPing: any = null;
    private socketTimer: any = null;
    private socketRetry = 0;
    /** true once a disconnect was asked for, so a close does not reconnect. */
    private socketClosed = true;
    private notificationSound: HTMLAudioElement | null = null;

    constructor() {
        this.initNotificationSound();
    }

    /**
     * Initialize notification sound
     */
    private initNotificationSound(): void {
        try {
            this.notificationSound = new Audio('assets/sounds/notification.mp3');
            this.notificationSound.volume = 0.5;
        } catch (error) {
            console.error('Failed to initialize notification sound:', error);
        }
    }

    /**
     * Play notification sound
     */
    private playNotificationSound(): void {
        try {
            if (this.notificationSound) {
                this.notificationSound.currentTime = 0;
                this.notificationSound.play().catch(err => {
                    console.warn('Could not play notification sound:', err);
                });
            }
        } catch (error) {
            console.error('Error playing notification sound:', error);
        }
    }

    /**
     * Show beautiful centered toast notification
     */
    showToastNotification(title: string, message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info'): void {
        // Remove existing toast if any
        const existingToast = document.querySelector('.custom-notification-toast');
        if (existingToast) {
            existingToast.remove();
        }

        // Create toast container
        const toast = document.createElement('div');
        toast.className = 'custom-notification-toast';

        // Icon based on type
        const icons: Record<string, string> = {
            success: '✓',
            info: '🔔',
            warning: '⚠',
            error: '✕'
        };

        toast.innerHTML = `
            <div class="toast-content">
                <div class="toast-icon ${type}">${icons[type]}</div>
                <div class="toast-text">
                    <div class="toast-title">${escapeHtml(title)}</div>
                    <div class="toast-message">${escapeHtml(message)}</div>
                </div>
                <button class="toast-close" onclick="this.parentElement.parentElement.remove()">✕</button>
            </div>
            <div class="toast-progress"></div>
        `;

        // Add styles if not already added
        this.addToastStyles();

        // Add to body
        document.body.appendChild(toast);

        // Trigger animation
        setTimeout(() => toast.classList.add('show'), 10);

        // Auto remove after 5 seconds
        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => toast.remove(), 300);
        }, 5000);
    }

    /**
     * Add toast styles to document
     */
    private addToastStyles(): void {
        if (document.getElementById('notification-toast-styles')) return;

        const styles = document.createElement('style');
        styles.id = 'notification-toast-styles';
        styles.textContent = `
            .custom-notification-toast {
                position: fixed;
                top: 15px;
                left: 50%;
                transform: translateX(-50%) translateY(-120%);
                z-index: 99999;
                background: #ffffff;
                border-radius: 10px;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
                min-width: 280px;
                max-width: 340px;
                opacity: 0;
                transition: all 0.3s ease-out;
                overflow: hidden;
                border: 1px solid #e5e7eb;
            }

            .custom-notification-toast.show {
                opacity: 1;
                transform: translateX(-50%) translateY(0);
            }

            .toast-content {
                display: flex;
                align-items: center;
                padding: 12px 14px;
                gap: 10px;
            }

            .toast-icon {
                width: 32px;
                height: 32px;
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                flex-shrink: 0;
            }

            .toast-icon.info {
                background: #5304fb;
                color: white;
            }

            .toast-icon.success {
                background: #10b981;
                color: white;
            }

            .toast-icon.warning {
                background: #f59e0b;
                color: white;
            }

            .toast-icon.error {
                background: #ef4444;
                color: white;
            }

            .toast-text {
                flex: 1;
                min-width: 0;
            }

            .toast-title {
                font-size: 13px;
                font-weight: 600;
                color: #1f2937;
                margin-bottom: 2px;
                line-height: 1.2;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .toast-message {
                font-size: 12px;
                color: #6b7280;
                line-height: 1.3;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            .toast-close {
                background: none;
                border: none;
                font-size: 14px;
                color: #9ca3af;
                cursor: pointer;
                padding: 0;
                width: 22px;
                height: 22px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 6px;
                transition: all 0.2s;
                flex-shrink: 0;
            }

            .toast-close:hover {
                background: #f3f4f6;
                color: #374151;
            }

            .toast-progress {
                height: 3px;
                background: linear-gradient(90deg, #5304fb 0%, #7c3aed 100%);
                animation: progress 5s linear forwards;
            }

            @keyframes progress {
                from { width: 100%; }
                to { width: 0%; }
            }
        `;
        document.head.appendChild(styles);
    }

    /**
     * Open the live channel — the gateway's own WebSocket.
     *
     * The session token goes in the query string because a browser's WebSocket constructor
     * cannot set headers. The server verifies it and binds the connection to that identity, so
     * a frame arriving here was addressed to THIS business by the server, not selected by us.
     */
    initRealtime(): void {
        if (this.socket) return; // already connected
        this.socketClosed = false;
        this.openSocket();
    }

    private socketUrl(): string | null {
        let token = '';
        try {
            token = localStorage.getItem('msToken') || '';
        } catch {
            token = '';
        }
        if (!token) return null; // signed out — nothing to listen as

        const origin = String(environment.microUrl || '').replace(/\/+$/, '');
        if (!origin) return null;
        const ws = origin.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
        return ws + '/ws?token=' + encodeURIComponent(token);
    }

    private openSocket(): void {
        const url = this.socketUrl();
        if (!url) return;

        try {
            const socket = new WebSocket(url);
            this.socket = socket;

            socket.onopen = () => {
                this.socketRetry = 0;
                // A ping every 25s. Proxies drop idle WebSockets long before anything notices,
                // and a dead socket looks exactly like a quiet one — this tells them apart.
                this.socketPing = setInterval(() => {
                    try {
                        socket.send(JSON.stringify({ type: 'ping' }));
                    } catch {
                        /* the close handler reconnects */
                    }
                }, 25000);
            };

            socket.onmessage = (ev: MessageEvent) => {
                let frame: any;
                try {
                    frame = JSON.parse(ev.data);
                } catch {
                    return;
                }
                if (frame?.type !== 'notification') return;
                // The same handler the Pusher binding used, so every screen behaves as before.
                this.handleRealtimeEvent(String(frame.event || 'client'), frame.data);
            };

            socket.onclose = () => {
                this.clearSocket();
                if (this.socketClosed) return; // closed on purpose
                // Capped backoff: a gateway restart must not become a request flood, and a
                // laptop waking from sleep should be back within a second.
                this.socketRetry = Math.min(this.socketRetry + 1, 6);
                const delay = Math.min(1000 * Math.pow(2, this.socketRetry - 1), 30000);
                this.socketTimer = setTimeout(() => this.openSocket(), delay);
            };

            socket.onerror = () => {
                // onclose always follows and owns the reconnect.
            };
        } catch (error) {
            console.error('Failed to open the live channel:', error);
        }
    }

    private clearSocket(): void {
        if (this.socketPing) {
            clearInterval(this.socketPing);
            this.socketPing = null;
        }
        this.socket = null;
    }

    /**
     * Handle one frame from the live channel
     */
    private handleRealtimeEvent(eventName: string, data: any): void {
        try {
            // Xano's Pusher channel carried its own internal events; nothing sends these now,
            // and the guard is kept because a stray frame must not be treated as a notification.
            if (eventName && (eventName.startsWith('pusher:') || eventName.startsWith('pusher_internal:'))) {
                return;
            }

            const parsed = typeof data === 'string' ? JSON.parse(data) : data;

            const contestLifecycleType = this.getContestLifecycleType(eventName, parsed);
            const isContestLifecycle = !!contestLifecycleType;

            // ✅ Filter: Only process client notifications (allow contest lifecycle events even if send_to is missing)
            if (parsed?.send_to && parsed?.send_to !== 'client' && !isContestLifecycle) {
                return;
            }

            // Get current user data from localStorage
            const currentUser = this.getCurrentUser();
            if (!currentUser) {
                return;
            }

            // Normalize contest lifecycle events to notification shape
            let normalized = parsed as NotificationData;
            if (isContestLifecycle) {
                normalized = {
                    ...normalized,
                    ...this.buildContestLifecycleNotification(contestLifecycleType!, normalized)
                } as NotificationData;
            }

            // Ensure minimum fields exist
            normalized.notification_name = normalized.notification_name || normalized.type || eventName || 'notification';
            normalized.notification_title = normalized.notification_title || normalized.notification_name || 'New Notification';
            normalized.message = formatPlanUpdateMessage(normalized) || 'You have a new notification';
            normalized.send_to = normalized.send_to || 'client';
            normalized.created_at = this.normalizeTimestamp(normalized.created_at) ?? Date.now();

            // Apply filtering logic
            const shouldShow = this.shouldShowNotification(normalized, currentUser);

            if (shouldShow) {
                // Assign a temporary negative ID if missing, so it can be tracked
                if (!normalized.id) {
                    normalized.id = -(Date.now());
                }

                // Add to realtime notifications observable
                const currentNotifications = this.realtimeNotifications.value;
                this.realtimeNotifications.next([normalized, ...currentNotifications]);

                // ✅ Track separately so API fetch doesn't wipe it
                this.realtimeOnlyNotifs = [normalized, ...this.realtimeOnlyNotifs];

                // ✅ Add to main notification list (for notification page)
                this.notif = [normalized, ...this.notif];

                // Increment unread count
                this.totalNewNotifications.next(this.totalNewNotifications.value + 1);

                // 🔔 Play notification sound
                this.playNotificationSound();

                // 🎨 Show beautiful centered toast
                const title = localizedNotificationTitle(normalized, (key) => this.translate?.instant(key))
                    || 'New Notification';
                this.showToastNotification(title, normalized.message || 'You have a new notification', 'info');

                // Show browser notification
                this.showBrowserNotification(title, normalized.message);

            } else {
            }
        } catch (error) {
            console.error('Error handling a live notification:', error);
        }
    }

    /**
     * Get current user data from localStorage
     */
    private getCurrentUser(): UserData | null {
        const userDetails = this.secureStorage.getItem<any>('userDetails');
        if (!userDetails) {
            return null;
        }

        try {
            const user = userDetails;

            // Get all company IDs owned by this user
            const companyIds: number[] = [];
            if (user.isCompany) {
                if (Array.isArray(user.isCompany)) {
                    user.isCompany.forEach((company: any) => {
                        if (company && company.id !== undefined) {
                            companyIds.push(company.id);
                        }
                    });
                } else if (typeof user.isCompany === 'object' && user.isCompany?.id !== undefined) {
                    companyIds.push(user.isCompany.id);
                }
            }
            if (user.company_id !== undefined && user.company_id !== null) {
                companyIds.push(user.company_id);
            }
            if (user.companyId !== undefined && user.companyId !== null) {
                companyIds.push(user.companyId);
            }

            return {
                id: user.id || 0,
                country: user.country?.toLowerCase() || '',
                created_at: user.created_at || Date.now(),
                Status: user.Status?.toLowerCase() || user.status?.toLowerCase() || 'active',
                companyIds: companyIds
            };
        } catch (error) {
            console.error('Error parsing user details:', error);
            return null;
        }
    }

    /**
     * Filter notification based on user attributes
     * This is the core filtering logic from your provided code
     */
    private shouldShowNotification(notification: NotificationData, currentUser: UserData): boolean {
        const client_id = currentUser.id;
        const user_country = currentUser.country || '';
        const user_created_at = currentUser.created_at;
        const user_status = currentUser.Status || '';
        const current_time = Date.now();

        // Consider user "new" if created within last 30 days (30 * 24 * 60 * 60 * 1000)
        const is_new_user = (current_time - user_created_at) < 2592000000;

        // Check send_to field - if it's "user", this is for employees/users, not clients
        if (notification.send_to === 'user') {
            return false;
        }

        // Handle custom_segment filtering
        if (notification.custom_segment && notification.custom_segment !== '') {
            switch (notification.custom_segment) {
                case 'everyone':
                    // Show to all users - no filtering needed
                    return true;

                case 'active':
                    if (user_status !== 'active') {
                        return false;
                    }
                    break;

                case 'inactive':
                    if (user_status !== 'inactive') {
                        return false;
                    }
                    break;

                case 'location':
                    // Location-based filtering - check location array
                    if (!notification.location || notification.location.length === 0) {
                        return false;
                    }
                    const notification_locations = notification.location.map(loc => loc.toLowerCase());
                    if (!notification_locations.includes(user_country)) {
                        return false;
                    }
                    break;

                case 'new_users':
                    if (!is_new_user) {
                        return false;
                    }
                    break;

                case 'specific_user':
                    // Check if notification has specific client_ids targeting
                    if (!notification.client_ids || notification.client_ids.length === 0) {
                        return false;
                    }

                    // Check if user's ID matches
                    const specificUserIdMatches = notification.client_ids.includes(client_id);

                    // Check if any of user's company IDs match
                    let specificCompanyIdMatches = false;
                    if (currentUser.companyIds && currentUser.companyIds.length > 0) {
                        specificCompanyIdMatches = notification.client_ids.some(id =>
                            currentUser.companyIds!.includes(id)
                        );
                    }

                    if (!specificUserIdMatches && !specificCompanyIdMatches) {
                        return false;
                    }
                    break;

                default:
                    // ✅ FIX: Handle plan-based segments (plan_30, plan_60, etc.)
                    if (notification.client_ids && notification.client_ids.length > 0) {
                        // Check if user's ID matches
                        const userIdMatches = notification.client_ids.includes(client_id);

                        // Check if any of user's company IDs match
                        let companyIdMatches = false;
                        if (currentUser.companyIds && currentUser.companyIds.length > 0) {
                            companyIdMatches = notification.client_ids.some(id =>
                                currentUser.companyIds!.includes(id)
                            );
                        }

                        // If neither user ID nor company ID matches, filter out
                        if (!userIdMatches && !companyIdMatches) {
                            return false;
                        }
                    }
                    break;
            }
        } else {
            // No custom_segment specified - Apply DEFAULT filtering (client_ids and location)

            // Check if notification has specific client_ids targeting
            if (notification.client_ids && notification.client_ids.length > 0) {
                // Check if user's ID matches
                const userIdMatches = notification.client_ids.includes(client_id);

                // Check if any of user's company IDs match
                let companyIdMatches = false;
                if (currentUser.companyIds && currentUser.companyIds.length > 0) {
                    companyIdMatches = notification.client_ids.some(id =>
                        currentUser.companyIds!.includes(id)
                    );
                }

                // If neither user ID nor company ID matches, filter out
                if (!userIdMatches && !companyIdMatches) {
                    return false;
                }
            }

            // Check location filtering
            if (notification.location && notification.location.length > 0 && notification.location[0] !== '') {
                const notification_locations = notification.location.map(loc => loc.toLowerCase());
                if (!notification_locations.includes(user_country)) {
                    return false;
                }
            }
        }

        return true;
    }

    private getContestLifecycleType(eventName: string, data: any): 'contest_approved' | 'contest_started' | 'contest_ended' | 'winner_announced' | null {
        const rawType = String(data?.type || data?.notification_name || eventName || '').toLowerCase();
        const normalized = rawType.replace(/[.\s-]+/g, '_');

        if (normalized.includes('contest') && normalized.includes('approved')) return 'contest_approved';
        if (normalized.includes('contest') && (normalized.includes('start') || normalized.includes('started'))) return 'contest_started';
        if (normalized.includes('contest') && (normalized.includes('end') || normalized.includes('ended') || normalized.includes('finish'))) return 'contest_ended';
        if (normalized.includes('winner') && (normalized.includes('announce') || normalized.includes('announced'))) return 'winner_announced';

        return null;
    }

    private buildContestLifecycleNotification(
        type: 'contest_approved' | 'contest_started' | 'contest_ended' | 'winner_announced',
        data: any
    ): Partial<NotificationData> {
        const contestName =
            data?.contest_name ||
            data?.contest?.name ||
            data?.name ||
            data?.contest_title ||
            data?.title ||
            'your campaign';

        const startDate = this.normalizeTimestamp(data?.start_date || data?.startDate || data?.contest?.start_date);
        const endDate = this.normalizeTimestamp(data?.end_date || data?.endDate || data?.contest?.end_date);
        const announceDate = this.normalizeTimestamp(
            data?.prize_announce_date ||
            data?.prize_announcement_date ||
            data?.contest?.prize_announce_date ||
            data?.contest?.prize_announcement_date
        );

        let notification_title = '';
        let message = '';

        switch (type) {
            case 'contest_approved':
                notification_title = 'Campaign Approved';
                message = startDate
                    ? `${contestName} is approved and will start on ${this.formatShortDate(startDate)}.`
                    : `${contestName} has been approved.`;
                break;
            case 'contest_started':
                notification_title = 'Campaign Started';
                message = `${contestName} is now live.`;
                break;
            case 'contest_ended':
                notification_title = 'Campaign Ended';
                message = endDate
                    ? `${contestName} ended on ${this.formatShortDate(endDate)}.`
                    : `${contestName} has ended.`;
                break;
            case 'winner_announced':
                notification_title = 'Winner Announced';
                message = announceDate
                    ? `Winners announced for ${contestName} on ${this.formatShortDate(announceDate)}.`
                    : `Winners have been announced for ${contestName}.`;
                break;
        }

        return {
            notification_name: type,
            notification_title,
            message
        };
    }

    private normalizeTimestamp(value: any): number | null {
        if (!value) return null;

        if (value instanceof Date) {
            const dateValue = value.getTime();
            return Number.isFinite(dateValue) ? dateValue : null;
        }

        if (typeof value === 'string') {
            const parsedDate = Date.parse(value);
            if (!Number.isNaN(parsedDate)) {
                return parsedDate;
            }
        }

        const numeric = Number(value);
        if (!Number.isFinite(numeric)) return null;

        return numeric < 1e12 ? numeric * 1000 : numeric;
    }

    private formatShortDate(timestamp: number): string {
        const date = new Date(timestamp);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    }

    /**
     * Show browser notification (requires permission)
     */
    private showBrowserNotification(title: string, message: string): void {
        if (!('Notification' in window)) {
            return;
        }

        if (Notification.permission === 'granted') {
            new Notification(title, {
                body: message,
                icon: '/assets/logo.png' // Update with your logo path
            });
        } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    new Notification(title, {
                        body: message,
                        icon: '/assets/logo.png'
                    });
                }
            });
        }
    }

    /**
     * Close the live channel
     */
    disconnectRealtime(): void {
        this.socketClosed = true;
        this.socketRetry = 0;
        if (this.socketTimer) {
            clearTimeout(this.socketTimer);
            this.socketTimer = null;
        }
        const socket = this.socket;
        this.clearSocket();
        try {
            socket?.close();
        } catch {
            /* already closed */
        }
    }

    /**
     * Newest first across every source.
     */
    sortByNewest(list: any[]): any[] {
        return list.map(notification => ({
            ...notification,
            message: formatPlanUpdateMessage(notification)
        })).sort(
            (a, b) => (this.normalizeTimestamp(b?.created_at) || 0) - (this.normalizeTimestamp(a?.created_at) || 0)
        );
    }

    async getNotificationList(): Promise<void> {
        try {
            this.sharedService.showLoader();

            const response: any = await firstValueFrom(
                this.pagesService.getAll('GET_NOTIFICATION')
            );

            let apiNotifs: any[] = [];

            if (response && response.length > 0) {
                // Get current user for filtering
                const currentUser = this.getCurrentUser();

                if (currentUser) {
                    // Filter notifications based on user attributes
                    apiNotifs = this.filterNotificationsByUser(response, currentUser);
                    // Fallback: If client-side filter stripped everything from an authenticated response,
                    // keep response so user does not get an empty screen
                    if (apiNotifs.length === 0 && response.length > 0) {
                        apiNotifs = response.map((n: any) => ({
                            ...n,
                            _notification_logs_of_notifications: n._notification_logs_of_notifications || null
                        }));
                    }
                } else {
                    apiNotifs = response;
                }
            }

            // ✅ Merge: prepend real-time notifications that aren't already in API data
            const apiIds = new Set(apiNotifs.map((n: any) => n.id));
            const uniqueRealtime = this.realtimeOnlyNotifs.filter(rt => !apiIds.has(rt.id));
            this.notif = this.sortByNewest([...uniqueRealtime, ...apiNotifs]);

            // Reset the notification count before processing
            this.totalNewNotifications.next(0);

            for (let i = 0; i < this.notif.length; i++) {
                if (!this.notif[i]?._notification_logs_of_notifications?.is_read) {
                    this.totalNewNotifications.next(this.totalNewNotifications.value + 1);
                }
                // A missing delivery row means "never seen by this account". The id test used to be
                // `id > 0`, which excluded the negative stand-in ids of real-time-only entries —
                // and silently excluded EVERYTHING once ids became uuids, because
                // 'a1b2…' > 0 is false. Asking for a uuid says what was meant.
                if (!this.notif[i]?._notification_logs_of_notifications &&
                    typeof this.notif[i].id === 'string') {
                    const payload = {
                        id: 0,
                        notification_id: this.notif[i].id,
                        is_read: false,
                        is_delivered: true,
                        read_at: '',
                        delivered_at: Date.now(),
                    };

                    await this.updateNotification(payload);
                }
            }

        } catch (error: any) {
            this.sharedService.showError(error, 'An error occurred');
        } finally {
            this.sharedService.hideLoader();
        }
    }

    /**
     * Filter notifications by user location, status, new user, etc.
     */
    private filterNotificationsByUser(notifications: any[], currentUser: UserData): any[] {
        const client_id = currentUser.id;
        const user_country = currentUser.country || '';
        const user_created_at = currentUser.created_at;
        const user_status = currentUser.Status || 'active';
        const current_time = Date.now();

        // Consider user "new" if created within last 30 days
        const is_new_user = (current_time - user_created_at) < 2592000000;

        const matchesId = (list: any[] | undefined, target: any): boolean => {
            if (!list || !Array.isArray(list) || target === undefined || target === null) return false;
            const strTarget = String(target).trim().toLowerCase();
            return list.some(id => String(id).trim().toLowerCase() === strTarget);
        };

        const matchesAnyId = (list: any[] | undefined, targets: any[] | undefined): boolean => {
            if (!list || !Array.isArray(list) || !targets || !targets.length) return false;
            return targets.some(target => matchesId(list, target));
        };

        return notifications
            .filter(n => {
                // Check send_to field
                if (n.send_to === 'user') {
                    return false;
                }

                // Handle custom_segment filtering
                if (n.custom_segment && n.custom_segment !== '') {
                    switch (n.custom_segment) {
                        case 'everyone':
                            break;

                        case 'active':
                            if (user_status && user_status !== 'active') return false;
                            break;

                        case 'inactive':
                            if (user_status && user_status !== 'inactive') return false;
                            break;

                        case 'location':
                            if (!n.location || n.location.length === 0) {
                                return false;
                            }
                            if (user_country) {
                                const notification_locations = n.location.map((loc: string) => loc.toLowerCase());
                                if (!notification_locations.includes(user_country)) {
                                    return false;
                                }
                            }
                            break;

                        case 'new_users':
                            if (!is_new_user) return false;
                            break;

                        case 'specific_user':
                            if (!n.client_ids || n.client_ids.length === 0) {
                                return false;
                            }
                            {
                                const userIdMatches = matchesId(n.client_ids, client_id);
                                const companyIdMatches = matchesAnyId(n.client_ids, currentUser.companyIds);
                                if (!userIdMatches && !companyIdMatches) {
                                    return false;
                                }
                            }
                            break;

                        default:
                            // Handle plan-based segments
                            if (n.client_ids && n.client_ids.length > 0) {
                                const userIdMatches = matchesId(n.client_ids, client_id);
                                const companyIdMatches = matchesAnyId(n.client_ids, currentUser.companyIds);
                                if (!userIdMatches && !companyIdMatches) {
                                    return false;
                                }
                            }
                            break;
                    }
                } else {
                    // No custom_segment specified - apply default logic
                    if (n.client_ids && n.client_ids.length > 0) {
                        const userIdMatches = matchesId(n.client_ids, client_id);
                        const companyIdMatches = matchesAnyId(n.client_ids, currentUser.companyIds);
                        if (!userIdMatches && !companyIdMatches) {
                            return false;
                        }
                    }

                    if (user_country && n.location && n.location.length > 0 && n.location[0] !== '') {
                        const notification_locations = n.location.map((loc: string) => loc.toLowerCase());
                        if (!notification_locations.includes(user_country)) {
                            return false;
                        }
                    }
                }

                return true;
            })
            .map(n => {
                // Find the notification log for the current client
                const clientLog = n._notification_logs_of_notifications || null;

                // Return cleaned notification data preserving lifecycle & rejection fields
                return {
                    ...n,
                    id: n.id,
                    created_at: n.created_at,
                    notification_name: n.notification_name,
                    notification_title: n.notification_title,
                    message: n.message,
                    attachments: n.attachments,
                    deep_link: n.deep_link,
                    contest_id: n.contest_id,
                    custom_deep_link: n.custom_deep_link,
                    rejection_reason: n.rejection_reason || n.reason || n.reject_reason || '',
                    reason: n.rejection_reason || n.reason || n.reject_reason || '',
                    _notification_logs_of_notifications: clientLog
                };
            })
            .sort((a, b) => (this.normalizeTimestamp(b.created_at) || 0) - (this.normalizeTimestamp(a.created_at) || 0)); // Sort by newest first
    }

    async updateNotification(data: any): Promise<void> {
        try {
            const response: any = await firstValueFrom(
                this.pagesService.create('UPDATE_NOTIFICATION', data)
            );
        } catch (error: any) {
            this.sharedService.showError(error, 'Failed to update notification');
        }
    }

    /**
     * Mark all notifications as read with a single bulk API call
     * @param notificationIds - Array of notification IDs to mark as read
     */
    async markAllNotificationsRead(notificationIds: (number | string)[]): Promise<boolean> {
        try {
            const payload = {
                notification_ids: notificationIds
            };

            const response: any = await firstValueFrom(
                this.pagesService.create('MARK_ALL_NOTIFICATIONS_READ', payload)
            );

            return true;
        } catch (error: any) {
            this.sharedService.showError(error, 'Failed to mark notifications as read');
            return false;
        }
    }

}
